import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { buildApiUrl } from '../config/apiConfig';
import {
  checkCartLinesStock,
  isPhysicalCartLine,
  matchStockLine,
  notifyStockWarning,
} from '../utils/cartStock.js';
import { log, warn } from '../utils/logger';

const isBrowser = typeof window !== 'undefined';
const CART_KEY = 'cart';

const parseCart = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    warn('[CartContext] Error parsing stored cart payload', error);
    return [];
  }
};

const readStoredCart = () => {
  if (!isBrowser) return [];
  try {
    return parseCart(window.localStorage.getItem(CART_KEY));
  } catch (error) {
    warn('[CartContext] Unable to access localStorage cart', error);
    return [];
  }
};

const sanitiseCartItem = (item) => {
  if (!item || typeof item !== 'object') {
    throw new Error('Invalid cart item');
  }

  const safeQuantity = Number.isFinite(item.quantity) ? Math.max(1, Number(item.quantity)) : 1;
  const safePrice = Number.isFinite(item.price) ? Number(item.price) : Number(item.price) || 0;
  const safeName = String(item.name || 'Producto').slice(0, 120);
  const rawVid = item.itemVariantId ?? item.item_variant_id;
  const itemVariantId =
    rawVid != null && rawVid !== '' && !Number.isNaN(Number(rawVid)) ? Number(rawVid) : null;

  return {
    id: item.id ?? item.productId ?? item.referenceId ?? null,
    name: safeName,
    price: safePrice,
    image: typeof item.image === 'string' ? item.image : '',
    productType: item.productType || item.type || 'item',
    quantity: safeQuantity,
    itemVariantId,
    variantName:
      typeof item.variantName === 'string' && item.variantName.trim()
        ? item.variantName.trim().slice(0, 120)
        : '',
    referenceId: item.referenceId != null ? item.referenceId : null,
  };
};

const lineMatches = (cartItem, id, productType, itemVariantId) =>
  cartItem.id === id &&
  cartItem.productType === productType &&
  (cartItem.itemVariantId ?? null) === (itemVariantId ?? null);

const getCartCount = (cart) =>
  cart.reduce((total, item) => total + (Number.isFinite(item.quantity) ? item.quantity : 1), 0);

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readStoredCart());
  const [userCourses, setUserCourses] = useState([]);
  const [userCoursesLoading, setUserCoursesLoading] = useState(false);

  const updateCartState = useCallback((updater) => {
    setCart((previous) => {
      const nextCart = typeof updater === 'function' ? updater(previous) : updater;

      if (isBrowser) {
        try {
          window.localStorage.setItem(CART_KEY, JSON.stringify(nextCart));
        } catch (error) {
          warn('[CartContext] Unable to persist cart in localStorage', error);
        }
      }

      return nextCart;
    });
  }, []);

  // Función para verificar si un curso ya está adquirido
  const checkCourseOwned = useCallback(async (courseId, token) => {
    if (!token || !courseId) return false;
    
    try {
      const response = await fetch(buildApiUrl(`/api/user/courses`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const courses = data.courses || [];
        return courses.some(course => course.id === courseId || course.courseId === courseId);
      }
    } catch (error) {
      warn('[CartContext] Error verificando curso adquirido:', error);
    }
    
    return false;
  }, []);

  const addToCart = useCallback(
    async (item, token = null, onError = null) => {
      const normalisedItem = sanitiseCartItem(item);
      if (!normalisedItem.id) {
        warn('[CartContext] Ignoring cart item without identifier', item);
        return false;
      }

      const reportError = (message) => {
        if (onError) {
          onError(message);
        } else {
          notifyStockWarning(message);
        }
      };

      if (normalisedItem.productType === 'course' && token) {
        const courseId = normalisedItem.id || normalisedItem.referenceId;
        if (courseId) {
          const isOwned = await checkCourseOwned(courseId, token);
          if (isOwned) {
            const errorMessage = 'Ya tienes este curso. Ve a "Mis Cursos" para acceder a él.';
            warn('[CartContext]', errorMessage);
            reportError(errorMessage);
            return false;
          }
        }
      }

      let quantityToAdd = normalisedItem.quantity;

      if (isPhysicalCartLine(normalisedItem)) {
        const existingLine = cart.find((cartItem) =>
          lineMatches(
            cartItem,
            normalisedItem.id,
            normalisedItem.productType,
            normalisedItem.itemVariantId,
          ),
        );
        const prospectiveQuantity = (existingLine?.quantity || 0) + quantityToAdd;

        try {
          const { lines } = await checkCartLinesStock([
            {
              id: normalisedItem.id,
              productType: normalisedItem.productType,
              quantity: prospectiveQuantity,
              itemVariantId: normalisedItem.itemVariantId,
            },
          ]);
          const stockLine = matchStockLine(
            lines,
            normalisedItem.id,
            normalisedItem.productType,
            normalisedItem.itemVariantId,
          );

          if (!stockLine || stockLine.maxQuantity <= 0) {
            reportError(stockLine?.message || 'Este producto no tiene stock disponible.');
            return false;
          }

          if (prospectiveQuantity > stockLine.maxQuantity) {
            const remaining = stockLine.maxQuantity - (existingLine?.quantity || 0);
            if (remaining <= 0) {
              reportError(
                stockLine.message ||
                  `Solo hay ${stockLine.maxQuantity} unidad(es) disponible(s).`,
              );
              return false;
            }
            quantityToAdd = remaining;
            notifyStockWarning(
              `Se agregaron ${remaining} unidad(es) — stock máximo: ${stockLine.maxQuantity}.`,
            );
          }
        } catch (err) {
          warn('[CartContext] Error verificando stock:', err);
          reportError('No se pudo verificar el stock. Intentá de nuevo.');
          return false;
        }
      }

      const itemToStore = { ...normalisedItem, quantity: quantityToAdd };

      updateCartState((previous) => {
        const existingIndex = previous.findIndex((cartItem) =>
          lineMatches(
            cartItem,
            itemToStore.id,
            itemToStore.productType,
            itemToStore.itemVariantId,
          ),
        );

        if (existingIndex >= 0) {
          const next = [...previous];
          const existing = next[existingIndex];
          next[existingIndex] = {
            ...existing,
            quantity: (existing.quantity || 1) + itemToStore.quantity,
          };
          return next;
        }

        return [...previous, itemToStore];
      });

      return true;
    },
    [updateCartState, checkCourseOwned, cart],
  );

  const removeFromCart = useCallback(
    (id, productType, itemVariantId = null) => {
      updateCartState((previous) =>
        previous.filter(
          (item) =>
            !lineMatches(item, id, productType, itemVariantId),
        ),
      );
    },
    [updateCartState],
  );

  const clearCart = useCallback(() => {
    log('[CartContext] Limpiando carrito...');
    updateCartState([]);
    log('[CartContext] Carrito limpiado exitosamente');
  }, [updateCartState]);

  const updateQuantity = useCallback(
    async (id, productType, quantity, itemVariantId = null) => {
      if (!Number.isFinite(quantity) || quantity < 1) {
        removeFromCart(id, productType, itemVariantId);
        return false;
      }

      let nextQuantity = Math.max(1, quantity);

      if (isPhysicalCartLine({ productType })) {
        try {
          const { lines } = await checkCartLinesStock([
            { id, productType, quantity: nextQuantity, itemVariantId },
          ]);
          const stockLine = matchStockLine(lines, id, productType, itemVariantId);

          if (!stockLine || stockLine.maxQuantity <= 0) {
            notifyStockWarning(stockLine?.message || 'Este producto no tiene stock disponible.');
            removeFromCart(id, productType, itemVariantId);
            return false;
          }

          if (nextQuantity > stockLine.maxQuantity) {
            nextQuantity = stockLine.maxQuantity;
            notifyStockWarning(
              stockLine.message ||
                `Cantidad ajustada al stock disponible (${stockLine.maxQuantity}).`,
            );
          }
        } catch (err) {
          warn('[CartContext] Error verificando stock:', err);
          notifyStockWarning('No se pudo verificar el stock. Intentá de nuevo.');
          return false;
        }
      }

      updateCartState((previous) =>
        previous.map((item) =>
          lineMatches(item, id, productType, itemVariantId)
            ? { ...item, quantity: nextQuantity }
            : item,
        ),
      );
      return true;
    },
    [updateCartState, removeFromCart],
  );

  useEffect(() => {
    if (!isBrowser) return undefined;

    const handleStorageChange = (event) => {
      if (event.key !== CART_KEY) return;
      // Solo actualizar si el cambio viene de otra pestaña/ventana
      // (event.newValue será null si viene del mismo contexto)
      if (event.newValue !== null) {
        setCart(parseCart(event.newValue));
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const cartCount = useMemo(() => getCartCount(cart), [cart]);

  const contextValue = useMemo(
    () => ({
      cart,
      cartCount,
      addToCart,
      removeFromCart,
      clearCart,
      updateQuantity,
    }),
    [addToCart, cart, cartCount, clearCart, removeFromCart, updateQuantity],
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

